import type { ReactElement, ReactNode } from 'react';

import { HelpLabel } from './InfoTip';

/* The top of every screen: one h1, an optional lede, optional actions on the right.
 *
 * A component rather than a convention because the alternative was four screens each choosing their
 * own heading level and margin. The heading level matters beyond tidiness: a screen with an `h2` and
 * no `h1` is one a screen-reader user cannot orient in, and every screen picking its own is how that
 * happens without anybody noticing.
 */

export interface PageHeaderProps {
  readonly title: string;
  /* `| undefined` on every optional prop. `exactOptionalPropertyTypes` is on, so a caller
   * forwarding a conditional — `count={ready ? label : undefined}` — cannot pass explicit
   * undefined to a bare `prop?: T`, and every screen does exactly that. */
  /** One sentence on what this screen is for. Optional, but a screen with no lede should be obvious
   * enough not to need one — if it isn't, write the sentence rather than omitting it. */
  readonly lede?: string | undefined;
  readonly actions?: ReactNode | undefined;
  /** A right-aligned readout of context: which tenant, how old the figures are.
   *
   * **Separate from `actions` rather than folded into it**, because the two are different kinds of
   * thing and a header that mixed them would put a button and a timestamp on one baseline. `actions`
   * are controls; this is a statement about what is being looked at. A screen may have both. */
  readonly meta?: ReactNode | undefined;
  /** A one- or two-sentence explanation of the term, shown behind an "i" beside it. Explains
   * what the thing *is*; never state, and never something a reader must see to act. */
  readonly help?: string | undefined;
}

export function PageHeader({
  title,
  lede,
  actions,
  meta,
  help,
}: PageHeaderProps): ReactElement {
  return (
    <header className="lq-page-header">
      <div>
        <HelpLabel subject={title} help={help}>
          <h1 className="lq-page-header__title">{title}</h1>
        </HelpLabel>
        {lede !== undefined && <p className="lq-page-header__lede">{lede}</p>}
      </div>
      {meta !== undefined && <div className="lq-page-header__meta">{meta}</div>}
      {actions !== undefined && <div className="lq-page-header__actions">{actions}</div>}
    </header>
  );
}
